'use client';

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocale } from 'next-intl';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StructuredData from '@/components/StructuredData';
import EmailCtaPanel from '@/components/EmailCtaPanel';

export default function ContactPage() {
  const locale = useLocale();

  const content = {
    en: {
      hero: {
        badge: 'Free Consultation',
        title: 'Let\'s Talk About Your Business',
        subtitle: 'Send us an email and discover how AI automation can transform your workflows.',
      },
      benefits: {
        title: 'What You\'ll Get',
        items: [
          {
            icon: '🎯',
            title: 'Free Business Audit',
            description: 'We\'ll analyze your current processes and identify automation opportunities.',
          },
          {
            icon: '⚡',
            title: 'Custom Solutions',
            description: 'No cookie-cutter approaches. Every solution is tailored to your specific needs.',
          },
          {
            icon: '🚀',
            title: 'Fast Implementation',
            description: 'Most projects go live within 2-4 weeks. Start seeing results quickly.',
          },
          {
            icon: '💬',
            title: 'No Obligation',
            description: 'One conversation, zero pressure. Just honest advice about what\'s possible.',
          },
        ],
      },
      trustSignal: 'Free consultation, no obligation',
      trust: {
        title: 'Trusted by',
        testimonial: {
          quote: '"Promptive helped us automate our customer support, saving us 20+ hours per week. The team truly understands business needs."',
          name: 'Jonas K.',
          role: 'CEO, Tech Startup',
        },
      },
    },
    lt: {
      hero: {
        badge: 'Nemokama Konsultacija',
        title: 'Pakalbėkime Apie Jūsų Verslą',
        subtitle: 'Parašykite mums ir sužinokite, kaip AI automatizacija gali pakeisti jūsų darbo procesus.',
      },
      benefits: {
        title: 'Ką Gausite',
        items: [
          {
            icon: '🎯',
            title: 'Nemokamas Verslo Auditas',
            description: 'Išanalizuosime jūsų dabartinus procesus ir nustatysime automatizavimo galimybes.',
          },
          {
            icon: '⚡',
            title: 'Individualūs Sprendimai',
            description: 'Jokių šabloninių sprendimų. Kiekvienas projektas pritaikomas jūsų poreikiams.',
          },
          {
            icon: '🚀',
            title: 'Greitas Įgyvendinimas',
            description: 'Dauguma projektų startuoja per 2-4 savaites. Rezultatus matysite greitai.',
          },
          {
            icon: '💬',
            title: 'Be Įsipareigojimų',
            description: 'Vienas pokalbis, jokio spaudimo. Tik sąžiningi patarimai.',
          },
        ],
      },
      trustSignal: 'Nemokama konsultacija, be įsipareigojimų',
      trust: {
        title: 'Mumis pasitiki',
        testimonial: {
          quote: '"Promptive padėjo automatizuoti mūsų klientų aptarnavimą, sutaupant 20+ valandų per savaitę. Komanda tikrai supranta verslo poreikius."',
          name: 'Jonas K.',
          role: 'CEO, Tech Startup',
        },
      },
    },
  };

  const c = content[locale as keyof typeof content] || content.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <StructuredData type="organization" />
      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-8 bg-gradient-to-br from-purple-50 via-blue-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              {c.hero.badge}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {c.hero.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {c.hero.subtitle}
            </p>
          </motion.div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-10 right-10 w-48 h-48 bg-purple-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-blue-200 rounded-full opacity-20 blur-3xl"></div>
      </section>

      {/* Main Section - Side by Side */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Left - Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:sticky lg:top-24"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
                {c.benefits.title}
              </h2>

              <div className="space-y-4">
                {c.benefits.items.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                    className="group relative bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md hover:border-purple-200 transition-all duration-300 flex gap-5 items-start overflow-hidden"
                  >
                    {/* Subtle accent line on left */}
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-400 to-blue-400 rounded-l-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Step number badge */}
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-md shadow-purple-100 group-hover:scale-105 transition-transform duration-300">
                      <span className="text-white font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{item.icon}</span>
                        <h3 className="font-semibold text-gray-900 text-[15px]">{item.title}</h3>
                      </div>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                    </div>

                    {/* Decorative corner glow */}
                    <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-purple-100 rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-300 blur-xl" />
                  </motion.div>
                ))}
              </div>

              {/* Bottom trust signal */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="mt-6 flex items-center gap-3 px-5 py-3.5 bg-green-50 border border-green-100 rounded-xl"
              >
                <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-700 text-sm font-medium">
                  {c.trustSignal}
                </p>
              </motion.div>
            </motion.div>

            {/* Right - Email CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <EmailCtaPanel variant="light" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section — Logo Marquee */}
      <section className="py-16 bg-white border-t border-gray-100 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center text-sm font-semibold uppercase tracking-widest text-gray-400 mb-10"
          >
            {c.trust.title}
          </motion.p>
        </div>

        {/* Marquee track */}
        <div
          className="relative overflow-hidden"
          style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
          }}
        >
          <div className="marquee-track">
            {[0, 1, 2, 3].map((setIdx) => (
              <div key={setIdx} className="flex items-center gap-6 px-3">
                {[
                  { src: '/images/kilo.png', alt: 'Kilo Health' },
                  { src: '/images/rhea.png', alt: 'Rhea' },
                  { src: '/images/magnimoo.png', alt: 'Magnimoo' },
                  { src: '/images/cheats-pro-logo.png', alt: 'Cheats Pro' },
                  { src: '/images/lentvario-logo.png', alt: 'Lentvario Mediena' },
                  { src: '/images/rideon-logo.png', alt: 'Rideon' },
                ].map((logo) => (
                  <div
                    key={logo.alt}
                    className="flex-shrink-0 bg-gray-50 hover:bg-gray-100 rounded-xl px-6 h-20 w-36 flex items-center justify-center transition-colors duration-200"
                  >
                    <Image
                      src={logo.src}
                      alt={logo.alt}
                      width={100}
                      height={50}
                      className="max-h-12 w-auto object-contain transition-opacity duration-200"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

      </section>

      <Footer />
    </div>
  );
}
